import React, { useState, useEffect } from 'react';
import { 
  Phone, 
  Plus, 
  Search, 
  Filter,
  User,
  Heart,
  Edit,
  Trash2,
  Eye,
  Share2,
  AlertCircle,
  Star,
  Mail,
  MapPin
} from 'lucide-react';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';

interface EmergencyContact {
  id: string;
  name: string;
  relationship?: string;
  phone: string;
  phone_secondary?: string;
  email?: string;
  address?: string;
  is_primary: boolean;
  notes?: string;
  created_at: string;
}

export function MobileEmergencyContacts() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [contacts, setContacts] = useState<EmergencyContact[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFilter, setSelectedFilter] = useState('all');

  // Load emergency contacts
  useEffect(() => {
    const loadContacts = async () => {
      if (!user) return;

      try {
        setLoading(true);
        setError(null);

        const { data, error } = await supabase
          .from('emergency_contacts')
          .select('*')
          .eq('user_id', user.id)
          .order('is_primary', { ascending: false });

        if (error) throw error;
        setContacts(data || []);

      } catch (err) {
        console.error('Error loading emergency contacts:', err);
        setError('Failed to load emergency contacts');
      } finally {
        setLoading(false);
      }
    };

    loadContacts();
  }, [user]);

  const handleViewContact = (contactId: string) => {
    console.log('View contact:', contactId);
  };

  const handleCallContact = (phone: string) => {
    console.log('Call contact:', phone);
  };

  const handleEmailContact = (email?: string) => {
    if (email) {
      console.log('Email contact:', email);
    }
  };

  const handleEditContact = (contactId: string) => {
    console.log('Edit contact:', contactId);
  };

  const handleDeleteContact = (contactId: string) => {
    console.log('Delete contact:', contactId);
  };

  const handleAddContact = () => {
    console.log('Add new contact');
  };

  const handleSetPrimary = (contactId: string) => {
    console.log('Set primary contact:', contactId);
  };

  const filteredContacts = contacts.filter(contact => {
    const matchesSearch = contact.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         (contact.relationship && contact.relationship.toLowerCase().includes(searchQuery.toLowerCase()));
    const matchesFilter = selectedFilter === 'all' || 
                         (selectedFilter === 'primary' && contact.is_primary) ||
                         (selectedFilter === 'family' && contact.relationship && contact.relationship.toLowerCase().includes('family')) ||
                         (selectedFilter === 'medical' && contact.relationship && contact.relationship.toLowerCase().includes('doctor'));
    return matchesSearch && matchesFilter;
  });

  const primaryContacts = contacts.filter(c => c.is_primary);
  const familyContacts = contacts.filter(c => c.relationship && c.relationship.toLowerCase().includes('family'));

  if (loading) {
    return (
      <div className="p-4 pb-safe space-y-4">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded"></div>
          <div className="space-y-3">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-20 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 pb-safe space-y-4">
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
          <AlertCircle size={16} className="text-red-500" />
          <p className="text-red-600 text-sm">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 pb-safe space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Emergency Contacts</h2>
          <p className="text-sm text-gray-600">Manage your emergency contacts</p>
        </div>
        <Button size="sm" onClick={handleAddContact}>
          <Plus size={16} className="mr-2" />
          Add
        </Button>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 gap-3">
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
              <Star size={20} className="text-red-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Primary</p>
              <p className="text-lg font-semibold">{primaryContacts.length}</p>
            </div>
          </div>
        </Card>

        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <Heart size={20} className="text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Family</p>
              <p className="text-lg font-semibold">{familyContacts.length}</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Search */}
      <div className="relative">
        <Search size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          placeholder="Search contacts..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-idecide-primary focus:border-transparent"
        />
      </div>

      {/* Filter */}
      <div className="flex gap-2 overflow-x-auto">
        <Button
          variant={selectedFilter === 'all' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setSelectedFilter('all')}
          className="whitespace-nowrap"
        >
          All
        </Button>
        <Button
          variant={selectedFilter === 'primary' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setSelectedFilter('primary')}
          className="whitespace-nowrap"
        >
          Primary
        </Button>
        <Button
          variant={selectedFilter === 'family' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setSelectedFilter('family')}
          className="whitespace-nowrap"
        >
          Family
        </Button>
        <Button
          variant={selectedFilter === 'medical' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setSelectedFilter('medical')}
          className="whitespace-nowrap"
        >
          Medical
        </Button>
      </div>

      {/* Contacts List */}
      <div className="space-y-3">
        {filteredContacts.length === 0 ? (
          <Card className="p-8 text-center">
            <Phone size={48} className="text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Emergency Contacts</h3>
            <p className="text-gray-600 mb-4">Start by adding your first emergency contact</p>
            <Button onClick={handleAddContact}>
              <Plus size={16} className="mr-2" />
              Add Contact
            </Button>
          </Card>
        ) : (
          filteredContacts.map((contact) => (
            <Card key={contact.id} className="p-4">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <Phone size={16} className="text-red-500" />
                    <h3 className="font-medium text-gray-900">{contact.name}</h3>
                    {contact.is_primary && (
                      <span className="px-2 py-1 bg-red-100 text-red-600 text-xs rounded-full">
                        Primary
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-600">{contact.relationship || 'Contact'}</p>
                  <p className="text-xs text-gray-500 flex items-center gap-1">
                    <Phone size={12} />
                    {contact.phone}
                  </p>
                  {contact.email && (
                    <p className="text-xs text-gray-500 flex items-center gap-1">
                      <Mail size={12} />
                      {contact.email}
                    </p>
                  )}
                  {contact.address && (
                    <p className="text-xs text-gray-500 flex items-center gap-1">
                      <MapPin size={12} />
                      {contact.address}
                    </p>
                  )}
                  {contact.notes && (
                    <p className="text-xs text-gray-500 mt-1">{contact.notes}</p>
                  )}
                </div>
                <div className="flex gap-1">
                  <Button 
                    size="sm" 
                    variant="ghost"
                    onClick={() => handleCallContact(contact.phone)}
                  >
                    <Phone size={14} />
                  </Button>
                  {contact.phone_secondary && (
                    <Button 
                      size="sm" 
                      variant="ghost"
                      onClick={() => handleCallContact(contact.phone_secondary)}
                    >
                      <Phone size={14} />
                    </Button>
                  )}
                  {contact.email && (
                    <Button 
                      size="sm" 
                      variant="ghost"
                      onClick={() => handleEmailContact(contact.email)}
                    >
                      <Mail size={14} />
                    </Button>
                  )}
                  <Button 
                    size="sm" 
                    variant="ghost"
                    onClick={() => handleViewContact(contact.id)}
                  >
                    <Eye size={14} />
                  </Button>
                  <Button 
                    size="sm" 
                    variant="ghost"
                    onClick={() => handleEditContact(contact.id)}
                  >
                    <Edit size={14} />
                  </Button>
                </div>
              </div>
              
              {/* Primary Contact Badge */}
              {!contact.is_primary && (
                <div className="mt-3">
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => handleSetPrimary(contact.id)}
                    className="text-xs"
                  >
                    <Star size={12} className="mr-1" />
                    Set as Primary
                  </Button>
                </div>
              )}
            </Card>
          ))
        )}
      </div>
    </div>
  );
} 